import { ErrorType } from '@otter-ai/telemetry';
import type { BatchItem } from '@rpldy/shared';
import {
  useAbortItem,
  useBatchAddListener,
  useItemAbortListener,
  useItemCancelListener,
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
} from '@rpldy/uploady';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
// import { userFeaturesQueryCreator } from 'src/data/queryClient/queryCreators/userFeaturesQueryCreator';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { userFeaturesQuery } from '@/query/userFeaturesQueryCreator';
import { displayErrorToast } from '../Components/Toasts';
import {
  importFailedEvent,
  importFileStartedEvent,
  importS3AttemptEvent,
  importS3FailedEvent,
  importStartedEvent,
  importSuccessEvent,
} from '../tracking/tracking';
import type {
  ImportQuotas,
  UploadItem,
  UploadModalContextData,
} from '../types/types';
import {
  extractUploadQuotas,
  validateBatchItems,
  wouldExceedBizCCTrialLimit,
  wouldExceedImportQuota,
} from '../utils/utils';
import {
  useCancelAll,
  useCreateSpeechUpload,
  usePostUploadProcessing,
  useSpeechTranscriptionStatus,
} from './helperHooks';
import { useBatchTracking } from './useBatchTracking';

// Main hook for setting up Uploady event listeners
export function useUploadItems(
  uploadQuota: ImportQuotas,
  setImportQuota: (quota: ImportQuotas) => void,
  contextData: UploadModalContextData,
) {
  const [record, setRecord] = useState<Record<string, UploadItem>>({});
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  // Track count of items currently in staging/upload queue (use ref for synchronous access)
  const stagingCountRef = useRef(0);
  // Track which items have been added to staging (prevents race conditions with async state updates)
  const stagedItemsRef = useRef<Set<string>>(new Set());
  // Track items that finished uploading this session (import_usage in subscription cache is only
  // refreshed on modal close, so this bridges the gap for the biz cc trial quota check)
  const committedCountRef = useRef(0);
  const { processUploadedItem } = usePostUploadProcessing();
  const { initializeSpeech } = useSpeechTranscriptionStatus();
  const { data: userFeatures } = useQuery(userFeaturesQuery());
  const { data: subscription } = useQuery(subscriptionQuery());
  const { refetch } = useCreateSpeechUpload();
  const cancelAll = useCancelAll();
  const abortFn = useAbortItem();

  // Track multiple batches for import ended event
  const { initializeBatchesFromItems, markItemSuccess, markItemFailure } =
    useBatchTracking();

  const allowedImportFormats = userFeatures?.allowed_import_formats;

  const maxRecordingDuration = userFeatures?.max_recording_duration;

  useBatchAddListener(async ({ items }: { items: BatchItem[] }) => {
    const validatedItems = await validateBatchItems(
      items,
      abortFn,
      allowedImportFormats,
      maxRecordingDuration,
    );

    // Check if adding these items would exceed quota
    // Compare against current staging count (items waiting/uploading)
    const currentStaging = stagingCountRef.current;
    // For biz cc trial: subscription.import_usage is only refreshed on modal close, so also
    // include committed (finished this session) to get the true remaining slots
    const bizCCTrialEffectiveStaging =
      stagingCountRef.current + committedCountRef.current;
    if (
      wouldExceedImportQuota(
        uploadQuota,
        validatedItems.length,
        currentStaging,
      ) ||
      wouldExceedBizCCTrialLimit({
        subscription,
        itemCount: validatedItems.length,
        currentStagingCount: bizCCTrialEffectiveStaging,
      })
    ) {
      setQuotaExceeded(true);
      cancelAll(items);
      return;
    }

    // Add items to staging count and track them (synchronous and atomic)
    validatedItems.forEach((item) => {
      stagedItemsRef.current.add(item.id);
      stagingCountRef.current += 1;
    });

    // Reset quota exceeded state if validation passes
    setQuotaExceeded(false);

    // Initialize batch tracking for import ended event (group items by batchId)
    initializeBatchesFromItems(validatedItems);

    setRecord((prev) => {
      const newRecord = { ...prev };
      validatedItems.forEach((item) => {
        importFileStartedEvent(item);
        newRecord[item.id] = item;
      });
      return newRecord;
    });

    importStartedEvent(validatedItems);
  });

  // Note: importEndedEvent is now fired in useBatchTracking when all items complete (success, failure, or abort)

  const itemLifecycleCallback = useCallback(
    (item: BatchItem) => {
      setRecord((prev) => {
        if (!prev[item.id]) return prev;

        return {
          ...prev,
          [item.id]: item,
        };
      });
    },
    [setRecord],
  );

  useItemStartListener((item: BatchItem) => {
    importS3AttemptEvent(item);

    setRecord((prev) => {
      if (!prev[item.id]) return prev;

      return {
        ...prev,
        [item.id]: item,
      };
    });
  });

  useItemCancelListener((item: BatchItem) => {
    // Only decrement if item was actually added to staging
    if (stagedItemsRef.current.has(item.id)) {
      stagingCountRef.current -= 1;
      stagedItemsRef.current.delete(item.id);
    }
    itemLifecycleCallback(item);
  });

  useItemAbortListener((item: BatchItem) => {
    // Only decrement if item was actually added to staging
    if (stagedItemsRef.current.has(item.id)) {
      stagingCountRef.current -= 1;
      stagedItemsRef.current.delete(item.id);
    }

    importFailedEvent(item, ErrorType.Cancel);

    // Only mark failure if batchId exists
    if (item.batchId) {
      markItemFailure(item.batchId);
    }

    setRecord((prev) => {
      if (!prev[item.id]) return prev;

      return {
        ...prev,
        [item.id]: item,
      };
    });
  });

  useItemErrorListener((item: BatchItem) => {
    // Only decrement if item was actually added to staging
    if (stagedItemsRef.current.has(item.id)) {
      stagingCountRef.current -= 1;
      stagedItemsRef.current.delete(item.id);
    }

    importFailedEvent(item, ErrorType.importS3Fail);
    importS3FailedEvent(item);

    // Only mark failure if batchId exists
    if (item.batchId) {
      markItemFailure(item.batchId);
    }

    setRecord((prev) => {
      if (!prev[item.id]) return prev;

      return {
        ...prev,
        [item.id]: item,
      };
    });
  });

  useItemProgressListener(itemLifecycleCallback);

  useItemFinishListener(async (item: BatchItem) => {
    // Remove from staging count and tracking; increment committed count so the biz cc trial
    // check stays accurate even though import_usage in the subscription cache is stale
    if (stagedItemsRef.current.has(item.id)) {
      stagingCountRef.current -= 1;
      stagedItemsRef.current.delete(item.id);
      committedCountRef.current += 1;
    }

    const id = item.id;
    let processingFailed = false;

    try {
      const result = await processUploadedItem(item, contextData);

      setRecord((prev) => {
        if (!prev[id]) return prev;

        const updatedItem: UploadItem = { ...item };
        if (result) {
          const otid = result.otid;
          updatedItem.otid = otid;
        }

        return {
          ...prev,
          [id]: updatedItem,
        };
      });

      // Initialize speech status AFTER state update, not during
      if (result) {
        initializeSpeech(result.otid, item.file.name);
      }

      importSuccessEvent(item);
    } catch (_error) {
      processingFailed = true;
      // Processing failed, mark as failure
      if (item.batchId) {
        markItemFailure(item.batchId);
      }
    }

    // Always try to update quota, even if processing failed
    try {
      const { data, error } = await refetch();
      const freshQuota = extractUploadQuotas(data, error);
      setImportQuota(freshQuota);
    } catch (_error) {
      // Quota update failed - block further uploads and show error
      displayErrorToast(
        'Unable to verify upload quota. Please refresh and try again.',
      );
      setImportQuota({ ...uploadQuota, outOfMinutes: true });
    }

    // Track successful completion only if processing succeeded
    if (!processingFailed && item.batchId) {
      markItemSuccess(item.batchId);
    }
  });

  return {
    list: Object.values(record),
    isQuotaExceeded: quotaExceeded,
  };
}
